import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { log } from "../../logger";
import type { ContainerKindFilter } from "../lib/containerMedia";

export type ThemeMode = "light" | "dark" | "system";
export type Language = "en" | "de";
/**
 * How the scanner stage presents the camera frame: filled (`cover`), whole
 * frame with bars (`contain`), or only the central decode region scaled up
 * (`scan-area`). Device-global, like the theme.
 */
export type ScannerDisplay = "cover" | "contain" | "scan-area";

/** The three display modes, in UI order (single source for pickers). */
export const SCANNER_DISPLAYS: readonly ScannerDisplay[] = ["cover", "contain", "scan-area"];

export const PREFERENCES_STORAGE_KEY = "egalregal-preferences";
export const PREFERENCES_STORAGE_VERSION = 1;

const DEFAULT_MODE: ThemeMode = "system";
const DEFAULT_LANGUAGE: Language = "en";
const DEFAULT_SCANNER_DISPLAY: ScannerDisplay = "cover";

function isThemeMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

function isLanguage(value: unknown): value is Language {
  return value === "en" || value === "de";
}

function isScannerDisplay(value: unknown): value is ScannerDisplay {
  return value === "cover" || value === "contain" || value === "scan-area";
}

/** Shape check for the persisted container-kind filter. */
function isKindFilter(value: unknown): value is ContainerKindFilter {
  if (value === null) return true;
  return (
    Array.isArray(value) &&
    value.every((entry) => typeof entry === "string" && entry.length > 0)
  );
}

function isEntryMap(value: unknown): value is Record<string, string> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return Object.entries(value as Record<string, unknown>).every(
    ([k, v]) => typeof k === "string" && typeof v === "string",
  );
}

interface PreferencesState {
  mode: ThemeMode;
  language: Language;
  /** Opt-in deadline column/sort on both item lists (deadlines 02). Off on the
   *  first visit; the user turns the whole deadline view on when it matters. */
  itemDeadlineView: boolean;
  /**
   * Include the whole descendant subtree's own photos and memos in the
   * container view's media grid (container-media 02). Off on the first visit;
   * applied to every container view once enabled.
   */
  containerSubtreeMedia: boolean;
  /**
   * Which descendant container kinds contribute subtree media. `null` is the
   * "all kinds" sentinel: every kind present now and any kind added later.
   * An explicit array narrows the filter until the user widens it again.
   */
  containerKindFilter: ContainerKindFilter;
  /** Desktop nav collapse, global to the device (shell-redesign 08). */
  sidebarCollapsed: boolean;
  /** Scanner stage presentation (scanner-surface): cover, contain, scan-area. */
  scannerDisplay: ScannerDisplay;
  /** Last selected catalog entry per username; isolates shared browsers. */
  selectedEntryIdByUser: Record<string, string>;
  setMode: (mode: ThemeMode) => void;
  setLanguage: (language: Language) => void;
  setItemDeadlineView: (enabled: boolean) => void;
  setContainerSubtreeMedia: (enabled: boolean) => void;
  setContainerKindFilter: (kinds: ContainerKindFilter) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setScannerDisplay: (display: ScannerDisplay) => void;
  /** Remember the entry for a user; entryId undefined clears it. */
  setSelectedEntryId: (username: string, entryId?: string) => void;
  selectedEntryIdFor: (username?: string) => string | undefined;
}

function safeStorage() {
  return {
    getItem: (key: string): string | null => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        // Validate shape early: corrupt JSON or wrong shape falls back
        // to defaults without crashing (issue 01).
        const parsed: unknown = JSON.parse(raw);
        const state = (parsed as { state?: unknown })?.state;
        if (typeof state !== "object" || state === null) {
          log.warn({ key }, "[preferences] Ignoring wrong-shaped stored state; using defaults");
          localStorage.removeItem(key);
          return null;
        }
        return raw;
      } catch (err) {
        log.warn({ err, key }, "[preferences] Ignoring corrupt stored state; using defaults");
        try {
          localStorage.removeItem(key);
        } catch (cleanupErr) {
          log.debug({ err: cleanupErr, key }, "[preferences] Could not clear corrupt stored state");
        }
        return null;
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        localStorage.setItem(key, value);
      } catch (err) {
        // Storage full or unavailable: preferences stay in memory.
        log.debug({ err, key }, "[preferences] Could not persist preferences; keeping in-memory state");
      }
    },
    removeItem: (key: string): void => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        log.debug({ err, key }, "[preferences] Could not clear stored preferences");
      }
    },
  };
}

/**
 * Persisted UI preferences (ADR-0030): theme, language, the item deadline view,
 * the container view's sub-container media scope and kind filter, the sidebar
 * collapse, the scanner stage display and the per-user selected catalog entry.
 * Transient catalog/auth state stays in useAppStore. The search
 * engine is not a preference: the `/search` URL owns the mode (shell-redesign
 * 09, spec §5).
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      mode: DEFAULT_MODE,
      language: DEFAULT_LANGUAGE,
      itemDeadlineView: false,
      containerSubtreeMedia: false,
      containerKindFilter: null,
      sidebarCollapsed: false,
      scannerDisplay: DEFAULT_SCANNER_DISPLAY,
      selectedEntryIdByUser: {},
      setMode: (mode) => set({ mode }),
      setLanguage: (language) => set({ language }),
      setItemDeadlineView: (itemDeadlineView) => set({ itemDeadlineView }),
      setContainerSubtreeMedia: (containerSubtreeMedia) => set({ containerSubtreeMedia }),
      setContainerKindFilter: (containerKindFilter) => set({ containerKindFilter }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setScannerDisplay: (scannerDisplay) => set({ scannerDisplay }),
      setSelectedEntryId: (username, entryId) =>
        set((state) => {
          if (!username) return state;
          if (entryId === undefined) {
            if (!(username in state.selectedEntryIdByUser)) return state;
            const next = { ...state.selectedEntryIdByUser };
            delete next[username];
            return { selectedEntryIdByUser: next };
          }
          if (state.selectedEntryIdByUser[username] === entryId) return state;
          return { selectedEntryIdByUser: { ...state.selectedEntryIdByUser, [username]: entryId } };
        }),
      selectedEntryIdFor: (username) => {
        if (!username) return undefined;
        return get().selectedEntryIdByUser[username];
      },
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      version: PREFERENCES_STORAGE_VERSION,
      storage: createJSONStorage(safeStorage),
      partialize: (state) => ({
        mode: state.mode,
        language: state.language,
        itemDeadlineView: state.itemDeadlineView,
        containerSubtreeMedia: state.containerSubtreeMedia,
        containerKindFilter: state.containerKindFilter,
        sidebarCollapsed: state.sidebarCollapsed,
        scannerDisplay: state.scannerDisplay,
        selectedEntryIdByUser: state.selectedEntryIdByUser,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<PreferencesState>;
        return {
          ...current,
          mode: isThemeMode(p.mode) ? p.mode : DEFAULT_MODE,
          language: isLanguage(p.language) ? p.language : DEFAULT_LANGUAGE,
          itemDeadlineView:
            typeof p.itemDeadlineView === "boolean" ? p.itemDeadlineView : false,
          containerSubtreeMedia:
            typeof p.containerSubtreeMedia === "boolean" ? p.containerSubtreeMedia : false,
          containerKindFilter: isKindFilter(p.containerKindFilter)
            ? p.containerKindFilter
            : null,
          sidebarCollapsed:
            typeof p.sidebarCollapsed === "boolean" ? p.sidebarCollapsed : false,
          scannerDisplay: isScannerDisplay(p.scannerDisplay)
            ? p.scannerDisplay
            : DEFAULT_SCANNER_DISPLAY,
          selectedEntryIdByUser: isEntryMap(p.selectedEntryIdByUser) ? p.selectedEntryIdByUser : {},
        };
      },
    },
  ),
);
